import { useCallback, useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { PanelHeader } from "./PanelHeader";
import { EmptyState } from "../ui/empty-state";
import { SectionCard } from "../ui/section-card";
import {
  type AdminFeedbackDetail,
  type AdminFeedbackListItem,
  getAdminFeedback,
  listAdminFeedback,
  postAdminFeedbackComment,
} from "../../services/adminFeedback";
import { cn } from "../../lib/utils";

const STATUS_LABELS: Record<string, string> = {
  new: "New",
  triaged: "Triaged",
  issue_created: "Issue created",
  fix_in_progress: "Fix in progress",
  in_progress: "Fix in progress",
  pr_open: "PR open",
  awaiting_verification: "Awaiting verification",
  merged: "Merged",
  fixed_in_prod: "Fixed in prod",
  no_action: "No action",
  wont_fix: "Won't fix",
  closed: "Closed",
};

function relativeAge(iso: string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/**
 * Read-only sys_admin view of production app feedback (list, detail, status
 * timeline) with a comment composer. Status changes happen in the web admin
 * UI; this panel is for reviewing on the go while writing.
 */
export function AdminFeedbackPanel() {
  const [items, setItems] = useState<AdminFeedbackListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminFeedbackDetail | null>(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (cursor?: string | null) => {
    setBusy(true);
    setError(null);
    try {
      const page = await listAdminFeedback(cursor ? { cursor } : undefined);
      setItems((current) => (cursor ? [...current, ...page.items] : page.items));
      setNextCursor(page.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load feedback");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    getAdminFeedback(selectedId)
      .then((next) => {
        if (!cancelled) setDetail(next);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Unable to load detail");
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  async function submitComment() {
    if (!selectedId || !comment.trim()) return;
    setBusy(true);
    try {
      await postAdminFeedbackComment(selectedId, comment.trim());
      setComment("");
      setDetail(await getAdminFeedback(selectedId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Comment failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background" aria-label="App feedback">
      <PanelHeader
        title="App feedback"
        description="Production feedback from users — read-only except comments."
        actions={
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={busy}>
            Refresh
          </Button>
        }
        className="px-4"
      />

      <div className="flex min-h-0 flex-1">
        <aside
          className="flex w-[38%] min-w-64 max-w-sm flex-col gap-2.5 overflow-y-auto border-r border-border px-4 py-3.5"
          aria-label="Feedback list"
        >
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item.id}>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setSelectedId(item.id)}
                  className={cn(
                    "h-auto w-full flex-col items-stretch rounded-lg border border-border bg-card p-3 text-left text-[0.8125rem] transition-colors hover:bg-muted/40",
                    selectedId === item.id && "border-primary/50 bg-primary/10"
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={cn(
                        "text-xs font-medium",
                        item.feedbackType === "bug" ? "text-destructive" : "text-muted-foreground"
                      )}
                    >
                      {item.feedbackType}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {STATUS_LABELS[item.status] ?? item.status} · {relativeAge(item.createdAt)}
                    </span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-foreground">{item.message}</p>
                </Button>
              </li>
            ))}
          </ul>
          {!busy && items.length === 0 ? (
            <EmptyState compact title="No feedback yet" description="New production feedback will appear here." />
          ) : null}
          {nextCursor ? (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => void load(nextCursor)}>
              Load more
            </Button>
          ) : null}
        </aside>

        <main className="flex min-h-0 min-w-0 flex-1 flex-col gap-3.5 overflow-y-auto px-4.5 pt-3.5 pb-4.5">
          {!detail ? (
            <EmptyState
              title="Select feedback"
              description="Choose an item from the list to read the full message, timeline, and comments."
            />
          ) : (
            <>
              <SectionCard tone="muted" title="Report">
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span>{detail.feedbackType}</span>
                  <span>· {STATUS_LABELS[detail.status] ?? detail.status}</span>
                  <span>· priority {detail.priority}</span>
                  {detail.appArea ? <span>· {detail.appArea}</span> : null}
                  {detail.email ? <span>· {detail.email}</span> : null}
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{detail.message}</p>
                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                  {detail.githubIssueUrl ? (
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-xs"
                      onClick={() => void window.desktopV2Api.openExternalUrl(detail.githubIssueUrl as string)}
                    >
                      Issue #{detail.githubIssueNumber}
                    </Button>
                  ) : null}
                  {detail.prUrl ? (
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-xs"
                      onClick={() => void window.desktopV2Api.openExternalUrl(detail.prUrl as string)}
                    >
                      PR #{detail.prNumber}
                    </Button>
                  ) : null}
                </div>
              </SectionCard>

              {detail.attachments.length > 0 ? (
                <SectionCard title="Attachments">
                  <div className="flex flex-wrap gap-2">
                    {detail.attachments.map((attachment) => (
                      <img
                        key={attachment.id}
                        src={attachment.url}
                        alt={attachment.source}
                        className="h-24 w-24 cursor-pointer rounded border border-border object-cover"
                        onClick={() => void window.desktopV2Api.openExternalUrl(attachment.url)}
                      />
                    ))}
                  </div>
                </SectionCard>
              ) : null}

              <SectionCard title="Timeline">
                <ol className="space-y-1 text-xs text-muted-foreground">
                  {detail.statusEvents.map((event) => (
                    <li key={event.id}>
                      {new Date(event.createdAt).toLocaleString()} — {event.fromStatus ? `${event.fromStatus} → ` : ""}
                      {event.toStatus} ({event.actor}){event.note ? ` — ${event.note}` : ""}
                    </li>
                  ))}
                  {detail.statusEvents.length === 0 ? <li>No status changes yet.</li> : null}
                </ol>
              </SectionCard>

              <SectionCard title="Comments" className="flex min-h-0 flex-1 flex-col">
                <ul className="max-h-64 space-y-2 overflow-y-auto">
                  {detail.comments.map((entry) => (
                    <li key={entry.id} className="rounded border border-border bg-muted/40 p-2 text-sm">
                      <div className="text-xs text-muted-foreground">
                        {entry.authorType === "admin"
                          ? entry.author?.name || entry.author?.email || "Admin"
                          : entry.authorType === "agent"
                            ? `Agent · ${entry.agentName ?? "unknown"}`
                            : "System"}
                        {" · "}
                        {new Date(entry.createdAt).toLocaleString()}
                      </div>
                      <p className="mt-1 whitespace-pre-wrap">{entry.body}</p>
                    </li>
                  ))}
                  {detail.comments.length === 0 ? (
                    <li className="text-sm text-muted-foreground">No comments yet.</li>
                  ) : null}
                </ul>
                <div className="mt-3 flex gap-2">
                  <Input
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    placeholder="Add a comment…"
                  />
                  <Button disabled={busy || !comment.trim()} onClick={() => void submitComment()}>
                    Post
                  </Button>
                </div>
              </SectionCard>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
